import React, { useMemo } from 'react';
import {
  Card,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  FormGroup,
  Label,
  Input,
} from 'reactstrap';
import { PanelHeader } from 'js/components';
import {
  default as RubricTreemap,
  Glyph as RubricTreemapGlyph,
} from 'js/components/Charts/RubricTreemap';
import { ChartSelector } from 'js/components';
import RubricScores from 'js/components/Charts/RubricScores';
import {
  RubricAssessmentDifferential,
  RubricAssessmentDifferentialGlyph,
} from './RubricAssessmentDifferential';
import { transformActivityToAssessments } from './RubricAssessmentDifferential/transformActivityToAssessments';
import AssessmentHistoryRadialPieChart, {
  Glyph as RadialPieGlyph,
} from 'js/engines/SurveyAssessment/components/Review/AssessmentHistoryRadialPieChart';
import AssessmentSeriesRadarChart from 'js/engines/SurveyAssessment/components/Review/AssessmentSeriesRadarChart';
import { Glyph as RadarGlyph } from 'js/components/Charts/SeriesRadarChart';
import { getSummaryMetrics, getCategoryLookups } from 'js/utils/question-set';
import { Breadcrumbs } from 'js/components/Breadcrumbs';
import {
  Link,
  Redirect,
  Route,
  Switch,
  useLocation,
  useParams,
  useRouteMatch,
} from 'react-router-dom';
import TeamRubricAssessmentHistory from './TeamRubricAssessmentHistory';
import {
  effectiveWeightSchemeSelectValue,
  filterEvaluationQuestionSetsForCurrentView,
  weightSchemeOptionValue,
  weightOverridesSortedForPicker,
} from 'js/utils/rubric';

import styles from './TeamRubricComponent.module.scss';

function TeamRubricComponent({
  team = null,
  rubric = null,
  scoresData = null,
  loading = false,
  loadError = false,
  weightScheme = null,
  onWeightSchemeChange = () => {},
}) {
  const { workspaceId } = useParams();
  const match = useRouteMatch();
  const location = useLocation();
  const baseUrl = match.url.replace(/\/(scorecard|activity)$/, '');
  const questionSets = useMemo(
    () =>
      filterEvaluationQuestionSetsForCurrentView(
        scoresData?.evaluation?.question_sets,
        rubric?.document
      ),
    [scoresData, rubric?.document]
  );
  const assessmentInstances = useMemo(
    () =>
      (scoresData?.assessment_instances || []).map((instance) => ({
        ...instance,
        inserted_at: new Date(instance.inserted_at),
      })),
    [scoresData]
  );
  const assessmentScoreMaps = useMemo(
    () => scoresData?.assessment_score_maps || {},
    [scoresData]
  );
  const rubricAssessments = useMemo(() => {
    const transformed = transformActivityToAssessments(
      questionSets,
      assessmentInstances,
      assessmentScoreMaps
    );
    return [...transformed].reverse();
  }, [questionSets, assessmentInstances, assessmentScoreMaps]);
  const dataMap = useMemo(
    () => getSummaryMetrics(rubricAssessments),
    [rubricAssessments]
  );
  const categoryLookups = useMemo(
    () => getCategoryLookups(rubricAssessments, dataMap.categories),
    [rubricAssessments, dataMap]
  );
  const categoryColorMap = useMemo(() => {
    const map = {};
    categoryLookups.forEach((value, key) => {
      map[key] = value.palette;
    });
    return map;
  }, [categoryLookups]);
  const chartHeight = 220;
  const weightOverrides = weightOverridesSortedForPicker(rubric?.document);
  const hasWeightOverrides = weightOverrides.length > 0;
  const weightSchemeSelectValue = effectiveWeightSchemeSelectValue(
    weightScheme,
    rubric?.document
  );

  const headerContent = useMemo(
    () => (
      <div className="row mt-3">
        <div className="col-12">
          <label>Charts</label>
          <ChartSelector
            loading={loading}
            error={loadError}
            charts={[
              {
                label: RubricTreemapGlyph,
                component: RubricTreemap,
                buttonProps: { 'data-chart-type': 'rubric-treemap' },
                componentProps: {
                  height: chartHeight,
                },
              },
              {
                label: RadialPieGlyph,
                component: AssessmentHistoryRadialPieChart,
                buttonProps: { 'data-chart-type': 'radial-pie' },
              },
              {
                label: RadarGlyph,
                component: AssessmentSeriesRadarChart,
                buttonProps: { 'data-chart-type': 'radar' },
              },
              {
                label: RubricAssessmentDifferentialGlyph,
                component: RubricAssessmentDifferential,
                buttonProps: { 'data-chart-type': 'differential' },
              },
            ]}
            questionSets={questionSets}
            assessmentInstances={assessmentInstances}
            assessmentScoreMaps={assessmentScoreMaps}
            assessments={rubricAssessments}
            dataMap={dataMap}
            categoryLookups={categoryLookups}
            categoryColorMap={categoryColorMap}
          />
        </div>
      </div>
    ),
    [
      chartHeight,
      loading,
      loadError,
      questionSets,
      assessmentInstances,
      assessmentScoreMaps,
      rubricAssessments,
      dataMap,
      categoryLookups,
      categoryColorMap,
    ]
  );

  const breadcrumbs = [
    { label: 'Teams', to: `/w/${workspaceId}/teams` },
    ...(team?.id
      ? [{ label: team.name, to: `/w/${workspaceId}/teams/${team.id}` }]
      : []),
    ...(team?.id
      ? [{ label: 'Rubrics', to: `/w/${workspaceId}/teams/${team.id}/rubrics` }]
      : []),
  ];

  return (
    <div>
      <PanelHeader size="flex">
        <div className="panel-body-no-label px-4">
          <Breadcrumbs breadcrumbs={breadcrumbs} />
          <div className="content-fluid">
            <div className={styles['title-row']}>
              <h1 className={styles.title}>{rubric?.name || 'Rubric'}</h1>
              <FormGroup className={styles['weight-scheme']}>
                <Label for="team-rubric-detail-weight-scheme">
                  Weight Scheme
                </Label>
                <Input
                  id="team-rubric-detail-weight-scheme"
                  type="select"
                  bsSize="sm"
                  className="limited-width-lg"
                  value={weightSchemeSelectValue}
                  title={
                    hasWeightOverrides
                      ? undefined
                      : 'This rubric has no alternate weight schemes'
                  }
                  onChange={(ev) => onWeightSchemeChange(ev.target.value)}
                >
                  <option value="">Default weights</option>
                  {weightOverrides.map((override) => {
                    const optVal = weightSchemeOptionValue(override);
                    if (!optVal) return null;
                    return (
                      <option key={optVal} value={optVal}>
                        {override?.name || optVal}
                      </option>
                    );
                  })}
                </Input>
              </FormGroup>
            </div>
            {headerContent}
          </div>
        </div>
        <div className="container-fluid">
          <Nav className="tab-nav nav-underline">
            <NavItem>
              <NavLink
                tag={Link}
                data-view="activity"
                to={`${baseUrl}/activity`}
                active={location.pathname.endsWith('/activity')}
              >
                Activity
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                tag={Link}
                data-view="scorecard"
                to={`${baseUrl}/scorecard`}
                active={location.pathname.endsWith('/scorecard')}
              >
                Scorecard
              </NavLink>
            </NavItem>
          </Nav>
        </div>
      </PanelHeader>
      <div className="content">
        <Switch>
          <Route path={`${match.path}/activity`}>
            <Card>
              <CardBody>
                <TeamRubricAssessmentHistory
                  team={team}
                  questionSets={questionSets}
                  assessmentInstances={assessmentInstances}
                  assessmentScoreMaps={assessmentScoreMaps}
                  loading={loading}
                  loadError={loadError}
                />
              </CardBody>
            </Card>
          </Route>
          <Route path={`${match.path}/scorecard`}>
            <Card>
              <CardBody>
                {!loading && !loadError && !scoresData && (
                  <p className="text-muted mt-2 mb-0">
                    No rubric data available.
                  </p>
                )}
                {!loading && !loadError && scoresData && (
                  <RubricScores questionSets={questionSets} />
                )}
              </CardBody>
            </Card>
          </Route>
          <Route exact path={match.path}>
            <Redirect to={`${baseUrl}/activity`} />
          </Route>
        </Switch>
      </div>
    </div>
  );
}

export default TeamRubricComponent;
